import { BREAKPOINTS } from '@/js/lib/constants';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

export function useIsScrolled(
  elementRef: React.MutableRefObject<HTMLElement>
): [boolean, Dispatch<SetStateAction<boolean>>] {
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const el = elementRef.current;

  const handleScroll = (e: Event) => {
    const { target } = e;
    const { scrollTop, scrollHeight, offsetHeight } = target as HTMLElement;

    setIsScrolled(scrollTop + offsetHeight === scrollHeight);
  };

  useEffect(() => {
    if (!el) return;
    el.addEventListener('scroll', handleScroll);

    return () => el.removeEventListener('scroll', handleScroll);
  }, [elementRef, el]);

  return [isScrolled, setIsScrolled];
}

export function useIsBreakpoint(breakpoint: keyof typeof BREAKPOINTS = 'lg') {
  const media = window.matchMedia(`(min-width: ${BREAKPOINTS[breakpoint]}px)`);
  const [isDesktop, setIsDesktop] = useState<boolean>(media.matches);

  useEffect(() => {
    const updateSize = () => {
      setIsDesktop(media.matches);
    };

    updateSize();
    window.addEventListener('resize', updateSize);

    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, [media.matches]);

  return isDesktop;
}

export function useOutsideClick(selector: string, callback: () => void) {
  useEffect(() => {
    function handleClickOutside({ target }: MouseEvent) {
      const els = document.querySelectorAll(selector);
      let isInside = false;

      for (const el of Array.from(els)) {
        if (el.contains(target as Node)) {
          isInside = true;
          break;
        }
      }

      if (!isInside) callback();
    }

    document.addEventListener('mouseup', handleClickOutside);
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [selector, callback]);
}
